"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

import { CheeseburgerIcon } from "@/shared/icons/CheeseburgerIcon";
import { CloseIcon } from "@/shared/icons/CloseIcon";

import styles from "./HeaderBurger.module.css";

const MENU_ANIMATION_MS = 300;

type HeaderBurgerProps = {
  children: ReactNode;
};

type MenuState = "closed" | "open" | "closing";

export const HeaderBurger = ({ children }: HeaderBurgerProps) => {
  const [menuState, setMenuState] = useState<MenuState>("closed");
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isMenuVisible = menuState !== "closed";

  const open = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setMenuState("open");
  }, []);

  const close = useCallback(() => {
    setMenuState((state) => {
      if (state !== "open") return state;
      return "closing";
    });
  }, []);

  useEffect(() => {
    if (menuState !== "closing") return;

    closeTimeoutRef.current = setTimeout(() => {
      setMenuState("closed");
      closeTimeoutRef.current = null;
    }, MENU_ANIMATION_MS);

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, [menuState]);

  useEffect(() => {
    if (!isMenuVisible) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isMenuVisible, close]);

  const handleMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("a")) {
      close();
    }
  };

  const menuPortal = isMenuVisible
    ? createPortal(
          <div className={styles.overlay}>
            <div
              id="header-mobile-menu"
              className={clsx(
                styles.menu,
                menuState === "open" && styles.menuOpen,
                menuState === "closing" && styles.menuClosing,
              )}
              role="dialog"
              aria-modal="true"
              aria-label="Меню навигации"
            >
              <button
                  type="button"
                  className={styles.menuClose}
                  aria-label="Закрыть меню"
                  onClick={close}
              >
                <CloseIcon size={24} />
              </button>
              <div className={styles.menuContent} onClick={handleMenuClick}>
                {children}
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={styles.burger}>
      <button
        type="button"
        className={styles.burgerButton}
        aria-expanded={menuState === "open"}
        aria-controls="header-mobile-menu"
        aria-label="Открыть меню"
        onClick={open}
      >
        <CheeseburgerIcon size={32} />
      </button>

      {menuPortal}
    </div>
  );
};
